import { Close } from '@ahamove/icons';
import { shadow } from '@ahamove/tokens';
import type { ItemDescription } from '@ahm/api-wrappers-core';
import { FloatingLabelInput } from '@ahm/react-ui';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  em,
  Group,
  rem,
  ScrollArea,
  Stack,
  Title,
  Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { ContextModalProps } from '@mantine/modals';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useMemo, useState, useEffect, useCallback, Fragment } from 'react';
import SearchIcon from '@/assets/icons/search-icon.svg';
import CustomImage from '@/components/custom-image';
import { useGetItemTrend } from '@/features/booking/api/special-request';
import { getServiceType } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

// Constants
const DEBOUNCE_TIME = 300;
const SCROLL_AREA_MOBILE_HEIGHT = 'calc(100svh - 250px)';
const SCROLL_AREA_DESKTOP_HEIGHT = 400;

// Types
interface BikeItemDescriptionsListModalProps {
  typeList: ItemDescription[];
}

// Utility Functions
const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

const highlightText = (text: string, highlight: string, maxLength = 50) => {
  const truncatedText = truncateText(text, maxLength);
  const parts = truncatedText.split(new RegExp(`(${highlight})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={i} style={{ fontWeight: 700 }}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </span>
  );
};

// Components
interface SearchBarProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}

const SearchBar = ({ searchTerm, setSearchTerm }: SearchBarProps) => {
  return (
    <Box mt="sm" p="md">
      <FloatingLabelInput
        radius={12}
        label="Tìm loại hàng hóa"
        rightSection={
          searchTerm ? (
            <Close width={22} height={22} onClick={() => setSearchTerm('')} />
          ) : (
            <SearchIcon className="size-6 md:size-8" />
          )
        }
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </Box>
  );
};

const NotFoundMessage = () => {
  const isMobile = useMediaQuery(`(max-width: ${em(767)})`);
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <CustomImage
        src="/static/images/search-not-found.webp"
        width={121}
        height={129}
        alt="Not found"
      />
      <Text
        fz={14}
        c="neutral.7"
        ta="center"
        mt={isMobile ? rem(8) : rem(16)}
        px={rem(16)}
      >
        Không tìm thấy kết quả phù hợp
      </Text>
    </Box>
  );
};

interface ItemListProps {
  filteredTypeList: ItemDescription[];
  selectedList: ItemDescription[];
  handleTypeChange: (code: string) => void;
  highlightText: (text: string, highlight: string) => React.ReactNode;
  searchTerm: string;
  scrollAreaHeight: number | string;
  multipleChoice: boolean;
}

const ItemList = ({
  filteredTypeList,
  selectedList,
  handleTypeChange,
  highlightText,
  searchTerm,
  scrollAreaHeight,
}: ItemListProps) => {
  const isMobile = useMediaQuery(`(max-width: ${em(767)})`);

  return (
    <Box mb={rem(16)}>
      <ScrollArea
        styles={{
          root: {
            overflowX: 'hidden',
            height: scrollAreaHeight,
          },
        }}
      >
        <Stack gap={0}>
          {filteredTypeList?.length ? (
            filteredTypeList.map((item, idx) => (
              <Fragment key={item.code}>
                {idx > 0 ? <Divider /> : null}
                <Button
                  onClick={() => handleTypeChange(item.code)}
                  variant="subtle"
                  size="md"
                  py="md"
                  px={isMobile ? 'md' : rem(24)}
                  h="auto"
                  color="neutral"
                  fw={500}
                  fullWidth
                  radius={0}
                  justify="space-between"
                  rightSection={
                    <Checkbox
                      value={item.code}
                      checked={selectedList.some(
                        (desc) => desc?.code === item.code
                      )}
                      readOnly
                    />
                  }
                >
                  {highlightText(item.keyword, searchTerm)}
                </Button>
              </Fragment>
            ))
          ) : (
            <NotFoundMessage />
          )}
        </Stack>
      </ScrollArea>
    </Box>
  );
};

interface ConfirmButtonProps {
  handleConfirm: () => void;
  tCommon: (key: string) => string;
}

const ConfirmButton = ({ handleConfirm, tCommon }: ConfirmButtonProps) => {
  const isMobile = useMediaQuery(`(max-width: ${em(767)})`);
  return (
    <Group
      justify="flex-end"
      mt="auto"
      gap={8}
      p="md"
      pos={{ base: 'fixed', sm: 'sticky' }}
      bottom={0}
      left={0}
      w="100%"
      bg="white"
      styles={{
        root: {
          zIndex: 100,
          borderTop: isMobile
            ? 'none'
            : `1px solid var(--mantine-color-neutral-1)`,
          boxShadow: isMobile ? shadow.buttonDock : 'none',
        },
      }}
    >
      <Button fullWidth size="md" mih={rem(48)} onClick={handleConfirm}>
        {tCommon('confirm')}
      </Button>
    </Group>
  );
};

// Main Component
const BikeItemDescriptionsListModal = ({
  context,
}: ContextModalProps<BikeItemDescriptionsListModalProps>) => {
  const { itemDescriptions, setItemDescriptions } = useBookingStore(
    (state) => state
  );

  const pathname = usePathname();
  const { isTruckService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );

  const { data: trends } = useGetItemTrend();

  const isMobile = useMediaQuery(`(max-width: ${em(767)})`);
  const tCommon = useTranslations('common');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedList, setSelectedList] = useState<ItemDescription[]>(
    itemDescriptions || []
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, DEBOUNCE_TIME);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleClose = useCallback(() => context.closeAll(), [context]);

  const handleTypeChange = useCallback(
    (code: string) => {
      setSelectedList((prev) => {
        const isSelected = prev.some((item) => item.code === code);
        if (isSelected) {
          return prev.filter((item) => item.code !== code);
        }
        if (isTruckService) {
          const newItem = trends?.find((item) => item.code === code);
          return newItem ? [newItem as ItemDescription] : [];
        } else {
          const newItem = trends?.find((item) => item.code === code);
          return newItem ? [...prev, newItem as ItemDescription] : prev;
        }
      });
    },
    [isTruckService, trends]
  );

  const handleConfirm = useCallback(() => {
    setItemDescriptions(selectedList);
    handleClose();
  }, [setItemDescriptions, selectedList, handleClose]);

  const filteredTypeList = useMemo(
    () =>
      trends?.filter((item) =>
        item.keyword.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      ) ?? [],
    [trends, debouncedSearchTerm]
  );

  const renderSearchResult = useMemo(() => {
    if (filteredTypeList?.length === 0) return '';
    return searchTerm
      ? `Kết quả liên quan: "${searchTerm}"`
      : 'Tất cả mặt hàng';
  }, [filteredTypeList?.length, searchTerm]);

  const scrollAreaHeight = isMobile
    ? SCROLL_AREA_MOBILE_HEIGHT
    : SCROLL_AREA_DESKTOP_HEIGHT;

  return (
    <>
      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      <Title h={64} p={16} order={3}>
        {renderSearchResult}
      </Title>
      <ItemList
        filteredTypeList={filteredTypeList}
        selectedList={selectedList}
        handleTypeChange={handleTypeChange}
        highlightText={highlightText}
        searchTerm={searchTerm}
        scrollAreaHeight={scrollAreaHeight}
      />
      <ConfirmButton handleConfirm={handleConfirm} tCommon={tCommon} />
    </>
  );
};

export default BikeItemDescriptionsListModal;
